import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Info, 
  ArrowRight, 
  Ruler, 
  Clock, 
  Layers, 
  Paintbrush,
  AlertCircle
} from 'lucide-react';

const PricingSection = () => {
  const priceRanges = [
    {
      title: 'Single Room Reskim',
      range: '£300 - £600',
      description: 'Ceiling & walls in an average-sized room',
      note: 'Varies by room size and condition',
    },
    {
      title: 'Ceiling Only',
      range: '£200 - £400',
      description: 'Skim coat on ceiling, standard height room',
      note: 'Artex removal adds to the cost',
    },
    {
      title: 'Patch Repairs',
      range: '£80 - £250',
      description: 'Small to medium repairs and making good',
      note: 'Per area, multiple patches quoted together',
    },
  ];

  const pricingFactors = [
    { icon: Ruler, label: 'Room size & access', description: 'Larger rooms and difficult access take more time' },
    { icon: Layers, label: 'Surface preparation', description: 'Old plaster removal, Wallpaper Stripping' },
    { icon: Paintbrush, label: 'Current condition', description: 'Artex, damaged plaster, or damp issues' },
  ];

  const scrollToContact = (e) => {
    e.preventDefault();
    const element = document.querySelector('#contact');
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section 
      id="pricing" 
      className="py-20 lg:py-28 bg-white"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-red-600 font-semibold text-sm tracking-wider uppercase mb-4">
            Pricing Guide
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 font-['Manrope'] mb-6">
            Transparent Pricing
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Every job is different, and we provide detailed quotes based on your specific 
            requirements. Here are some typical ranges to give you an idea.
          </p>
        </div>

        {/* Price Range Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {priceRanges.map((item, index) => (
            <Card 
              key={index}
              className="border border-slate-100 shadow-sm hover:shadow-md hover:border-red-200 transition-all duration-300"
              data-testid={`price-card-${index}`}
            >
              <CardContent className="p-8 text-center">
                <h3 className="text-lg font-semibold text-slate-900 font-['Manrope'] mb-3">
                  {item.title}
                </h3>
                <p className="text-3xl font-bold text-red-600 font-['Manrope'] mb-3">
                  {item.range}
                </p>
                <p className="text-slate-600 mb-4">
                  {item.description}
                </p>
                <p className="text-sm text-slate-400 italic">
                  {item.note}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="flex items-start gap-4 bg-red-50 rounded-xl p-6 mb-12 border border-red-100">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-slate-900 font-['Manrope'] mb-2">
              Prices are estimates only
            </p>
            <p className="text-slate-600">
              These figures are provided as a general guide. Actual costs depend on the specific 
              requirements of your project. We always provide a detailed written quote before 
              any work begins, with no hidden fees or surprises.
            </p>
          </div>
        </div>

        {/* Factors Section */}
        <div className="bg-slate-50 rounded-2xl p-8 lg:p-12">
          <div className="text-center mb-10">
            <h3 className="text-2xl font-bold text-slate-900 font-['Manrope'] mb-3">
              What Affects the Price?
            </h3>
            <p className="text-slate-600">
              Several factors influence the final cost of your plastering project
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {pricingFactors.map((factor, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-6 text-center shadow-sm"
                data-testid={`pricing-factor-${index}`}
              >
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <factor.icon className="w-6 h-6 text-red-600" />
                </div>
                <h4 className="font-semibold text-slate-900 font-['Manrope'] mb-2">
                  {factor.label}
                </h4>
                <p className="text-sm text-slate-500">
                  {factor.description}
                </p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center">
            <p className="text-slate-600 mb-6">
              Want an accurate quote for your project? Send us some photos and measurements.
            </p>
            <Button
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 rounded-full shadow-lg"
              onClick={scrollToContact}
              data-testid="pricing-quote-btn"
            >
              Get Your Free Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
